<template>
  <div class="w-full space-y-3">
    <!-- Reviews Header Section -->
    <div
      class="relative overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-blue-950 dark:to-indigo-950 rounded-xl p-4 border border-slate-200/60 dark:border-slate-700/60 shadow-lg"
    >
      <!-- Background Pattern -->
      <div class="absolute inset-0 opacity-5 dark:opacity-10">
        <svg class="w-full h-full" viewBox="0 0 100 100" fill="none">
          <defs>
            <pattern id="reviews-pattern" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
              <circle cx="10" cy="10" r="1" fill="currentColor" />
            </pattern>
          </defs>
          <rect width="100" height="100" fill="url(#reviews-pattern)" />
        </svg>
      </div>

      <div class="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <!-- Title and Summary -->
        <div class="space-y-2">
          <div class="flex items-center gap-2">
            <div class="relative">
              <div
                class="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center shadow-md"
              >
                <MessageSquare class="w-4 h-4 text-white" />
              </div>
              <div class="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full flex items-center justify-center">
                <span class="text-xs font-bold text-white">{{ reviews?.length || 0 }}</span>
              </div>
            </div>
            <div>
              <h2
                class="text-base font-semibold bg-gradient-to-r from-slate-800 to-slate-600 dark:from-slate-100 dark:to-slate-300 bg-clip-text text-transparent"
              >
                {{ t('reviews.customer_reviews') }}
              </h2>
              <p class="text-xs text-slate-600 dark:text-slate-400">{{ t('reviews.see_what_customers_saying') }}</p>
            </div>
          </div>
          <!-- Rating Summary -->
          <div
            v-if="summary && summary.average_rating && Number(summary.average_rating) > 0"
            class="flex flex-col sm:flex-row sm:items-center gap-2"
          >
            <div
              class="flex items-center gap-2 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-lg px-3 py-2 shadow-md border border-white/20 dark:border-slate-700/20"
            >
              <div class="text-center">
                <div class="text-lg font-bold text-slate-900 dark:text-white">
                  {{ (Number(summary.average_rating) || 0).toFixed(1) }}
                </div>
                <SfRating :value="Number(summary.average_rating) || 0" :max="5" size="sm" class="text-amber-400" />
                <div class="text-xs text-slate-500 dark:text-slate-400">{{ t('reviews.average') }}</div>
              </div>
            </div>
            <div class="flex flex-col gap-1">
              <div class="flex items-center gap-1 text-slate-700 dark:text-slate-300">
                <span class="text-sm font-medium">
                  {{ t('reviews.based_on_reviews', { count: Number(summary.reviews_count) || 0 }) }}
                </span>
              </div>
              <div class="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                <div class="w-1 h-1 bg-emerald-400 rounded-full animate-pulse"></div>
                <span>{{ t('reviews.review_count', { count: Number(summary.reviews_count) || 0 }) }}</span>
              </div>
            </div>
          </div>
        </div>
        <!-- Write Review Button -->
        <div class="flex-shrink-0">
          <!-- Authenticated user who can review -->
          <Button v-if="isAuthenticated && canReview" @click="showWriteReviewForm" size="sm" class="btn btn-primary font-semibold">
            <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            {{ t('reviews.write_a_review') }}
          </Button>
          <!-- Authenticated user who cannot review (hasn't purchased) -->
          <div v-else-if="isAuthenticated && !canReview" class="text-center">
            <Button disabled variant="outline" size="sm" class="btn btn-secondary cursor-not-allowed font-semibold">
              <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 15v2m0 0v2m0-2h2m-2 0H10m4-12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {{ t('reviews.purchase_to_review') }}
            </Button>
          </div>
          <!-- Unauthenticated user -->
          <Button v-else @click="showWriteReviewForm" variant="outline" size="sm" class="btn btn-primary font-semibold">
            <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
              />
            </svg>
            {{ t('reviews.login_to_review') }}
          </Button>
        </div>
      </div>
    </div>

    <!-- Reviews Content -->
    <Card class="overflow-hidden border-0 shadow-lg bg-white/60 dark:bg-slate-800/60 backdrop-blur-xl rounded-xl">
      <CardContent class="p-0">
        <!-- Loading State -->
        <div v-if="loading" class="flex flex-col items-center justify-center py-8 px-4">
          <div class="relative">
            <div
              class="w-8 h-8 border-2 border-blue-200 dark:border-blue-800 rounded-full animate-spin border-t-blue-600 dark:border-t-blue-400"
            ></div>
            <div
              class="absolute inset-0 w-8 h-8 border-2 border-purple-200 dark:border-purple-800 rounded-full animate-spin animate-reverse border-r-purple-600 dark:border-r-purple-400"
            ></div>
          </div>
          <p class="mt-3 text-slate-600 dark:text-slate-400 font-medium text-sm">{{ t('reviews.loading_reviews') }}</p>
          <div class="mt-1 flex gap-1">
            <div class="w-1 h-1 bg-blue-400 rounded-full animate-bounce" style="animation-delay: 0ms"></div>
            <div class="w-1 h-1 bg-blue-400 rounded-full animate-bounce" style="animation-delay: 150ms"></div>
            <div class="w-1 h-1 bg-blue-400 rounded-full animate-bounce" style="animation-delay: 300ms"></div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="!reviews?.length" class="text-center py-8 px-4">
          <div class="relative inline-block mb-4">
            <div
              class="w-16 h-16 bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 dark:from-slate-700 dark:via-blue-900 dark:to-indigo-900 rounded-xl flex items-center justify-center shadow-inner border border-slate-200 dark:border-slate-600"
            >
              <MessageSquare class="w-8 h-8 text-slate-400 dark:text-slate-500" />
            </div>
            <div
              class="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-br from-orange-400 to-red-500 rounded-lg flex items-center justify-center shadow-lg transform rotate-12"
            >
              <span class="text-xs font-bold text-white">0</span>
            </div>
          </div>
          <h3 class="text-base font-semibold text-slate-900 dark:text-white mb-2">{{ t('reviews.no_reviews_yet') }}</h3>
          <p class="text-slate-600 dark:text-slate-400 max-w-lg mx-auto text-sm mb-4">
            <span v-if="isAuthenticated && canReview">{{ t('reviews.be_the_first_to_review') }}</span>
            <span v-else-if="isAuthenticated && !canReview">{{ t('reviews.purchase_required_message') }}</span>
            <span v-else>{{ t('reviews.be_the_first_to_review') }}</span>
          </p>
          <!-- Authenticated user who can review -->
          <Button
            v-if="isAuthenticated && canReview"
            @click="showWriteReviewForm"
            size="sm"
            class="bg-primary-600 hover:bg-primary-700 text-white"
          >
            <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
              />
            </svg>
            {{ t('reviews.write_first_review') }}
          </Button>
          <!-- Authenticated user who cannot review (purchase required) -->
          <div v-else-if="isAuthenticated && !canReview" class="text-center">
            <div
              class="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 rounded-lg border border-amber-200 dark:border-amber-700/50"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 15v2m0 0v2m0-2h2m-2 0H10m4-12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span class="text-sm font-medium">{{ t('reviews.purchase_required') }}</span>
            </div>
          </div>
        </div>

        <!-- Reviews List -->
        <div v-else class="divide-y divide-slate-300 dark:divide-slate-700/50 max-h-[60vh] overflow-y-auto">
          <div v-for="(review, index) in reviews" :key="review.id" class="group relative p-3">
            <!-- Hover Effect -->
            <div
              class="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
            ></div>

            <!-- Review Header -->
            <div class="relative flex items-start justify-between mb-2">
              <div class="flex-1 space-y-1">
                <!-- Review Title -->
                <div class="flex items-start gap-2">
                  <div class="relative flex-shrink-0">
                    <div
                      class="w-8 h-8 bg-gradient-to-br from-primary-500 via-gray-600 to-primary-600 rounded-lg flex items-center justify-center text-white font-bold text-xs shadow-md group-hover:scale-105 transition-transform duration-200"
                    >
                      {{ review.author?.charAt(0)?.toUpperCase() || 'U' }}
                    </div>
                    <div
                      class="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border border-white dark:border-slate-800 flex items-center justify-center"
                    >
                      <svg class="w-1.5 h-1.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fill-rule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                  <div class="flex-1">
                    <h4 class="text-sm font-semibold text-slate-900 dark:text-white leading-tight mb-1">
                      {{ review.title }}
                    </h4>
                    <div class="flex flex-col sm:flex-row sm:items-center gap-1">
                      <SfRating :value="Number(review.rating) || 0" :max="5" size="sm" class="text-amber-400" />
                      <div class="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                        <span class="font-medium">{{ review.author }}</span>
                        <span>•</span>
                        <time class="font-medium">{{ formatDate(review.date) }}</time>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Action Buttons -->
              <div v-if="isCurrentUserReview(review)" class="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  @click="showEditReviewForm(review)"
                  class="h-8 w-8 p-0 hover:bg-gray-500 text-gray-600 dark:hover:bg-gray-950/20 dark:text-gray-400"
                >
                  <Pencil class="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  @click="handleDeleteClick(review)"
                  class="h-8 w-8 p-0 hover:bg-red-500 text-red-600 dark:hover:bg-red-950/20 dark:text-red-400"
                >
                  <Trash class="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            <!-- Review Content -->
            <div class="relative pl-10 space-y-2">
              <div class="relative">
                <p class="text-slate-700 dark:text-slate-300 text-sm">{{ review.content }}</p>
                <div
                  class="absolute -left-10 top-1 w-0.5 h-3 bg-gradient-to-b from-blue-400 to-indigo-500 rounded-full opacity-30 group-hover:opacity-100 transition-opacity duration-300"
                ></div>
              </div>
              <!-- Additional Criteria -->
              <div
                v-if="review.criteria?.length"
                class="bg-gradient-to-r from-slate-50 to-blue-50 dark:from-slate-800/50 dark:to-blue-950/30 rounded-lg p-2 border border-slate-200 dark:border-slate-700"
              >
                <h5 class="text-xs font-semibold text-slate-900 dark:text-white mb-1 flex items-center gap-1">
                  <svg class="w-3 h-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                  {{ t('reviews.detailed_ratings') }}
                </h5>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-1">
                  <div
                    v-for="criterion in review.criteria"
                    :key="criterion.id_criteria"
                    class="flex items-center justify-between p-1.5 bg-white/60 dark:bg-slate-800/60 rounded-lg border border-slate-200/50 dark:border-slate-600/50 hover:shadow-sm transition-shadow duration-200"
                  >
                    <span class="text-xs font-medium text-slate-700 dark:text-slate-300">{{ criterion.name }}</span>
                    <SfRating :value="Number(criterion.rating) || 0" :max="5" size="xs" class="text-amber-400" />
                  </div>
                </div>
              </div>
              <!-- Owner Badge -->
              <div
                v-if="isCurrentUserReview(review)"
                class="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-primary-100 to-gray-100 dark:from-primary-900/30 dark:to-gray/30 text-primary-700 dark:text-primary-300 rounded-full text-xs font-medium border border-primary-200 dark:border-primary-700"
              >
                <svg class="w-2 h-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
                </svg>
                {{ t('reviews.your_review') }}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
    <!-- Delete Confirmation Dialog -->
    <Dialog :open="showDeleteConfirmation" @update:open="showDeleteConfirmation = false">
      <DialogContent aria-describedby="delete-review-description" class="sm:max-w-md">
        <DialogHeader class="text-center pb-3">
          <div class="mx-auto w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-3">
            <Trash class="w-6 h-6 text-red-600 dark:text-red-400" />
          </div>
          <DialogTitle class="text-lg font-semibold text-gray-900 dark:text-white">
            {{ t('reviews.confirm_delete_review') }}
          </DialogTitle>
          <DialogDescription id="delete-review-description" class="text-gray-600 dark:text-gray-400 mt-1 text-sm">
            {{ t('reviews.delete_review_confirmation') }}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter class="flex flex-col-reverse sm:flex-row gap-2 pt-3">
          <Button variant="outline" @click="showDeleteConfirmation = false" size="sm" class="flex-1 font-semibold">
            {{ t('reviews.cancel') }}
          </Button>
          <Button
            variant="destructive"
            :disabled="isDeleting"
            @click="confirmDeleteReview"
            size="sm"
            class="flex-1 font-semibold text-white"
          >
            <Loader2 v-if="isDeleting" class="mr-1 h-3 w-3 animate-spin" />
            {{ isDeleting ? t('reviews.deleting') : t('reviews.delete') }}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <!-- Product Review Form Dialog -->
    <ProductReviewForm
      :open="showForm"
      :review="currentReview"
      :criteria="criteria"
      :is-submitting="isSubmitting"
      :form-error="formError"
      @update:open="showForm = $event"
      @submit="handleSubmitReview"
      @cancel="hideForm"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { SfRating } from '@storefront-ui/vue';
import type { PropType } from 'vue';
import type { ProductReview, ProductReviewCriteria } from '@vue-storefront/prestashop-api';
import { useProductReviews } from '~/composables/useProductReviews';
import { useCustomer } from '~/composables/useCustomer';
import useNotifications from '~/stores/notifications';
import { Loader2, MessageSquare, Pencil, Trash } from 'lucide-vue-next';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import ProductReviewForm from '~/components/ProductReviewForm.vue';

const props = defineProps({
  productId: {
    type: [Number, String],
    required: true,
  },
});

// State
const showForm = ref(false);
const currentReview = ref<ProductReview | null>(null);
const isSubmitting = ref(false);
const showDeleteConfirmation = ref(false);
const isDeleting = ref(false);
const reviewToDelete = ref<ProductReview | null>(null);
const criteria = ref<ProductReviewCriteria[]>([
  { id_criteria: 1, name: 'Quality', rating: 0 },
  { id_criteria: 2, name: 'Value for money', rating: 0 },
  { id_criteria: 3, name: 'Packaging', rating: 0 },
]);
const formError = ref<string | null>(null);

// Composables
const { isAuthenticated, data: customer } = useCustomer();
const { t } = useI18n();
const { send } = useNotifications();
const { query, reviews, summary, canReview, fetchProductReviews, addProductReview, updateProductReview, deleteProductReview } =
  useProductReviews(props.productId);

// Extract loading state from TanStack Query
const loading = computed(() => query.isLoading || query.isFetching);

// Computed
const isEditing = computed(() => !!currentReview.value);

// Methods
const showWriteReviewForm = () => {
  if (!isAuthenticated.value) {
    const router = useRouter();
    const route = useRoute();
    router.push({
      path: '/login',
      query: { redirect: route.fullPath + '#reviews' },
    });
    return;
  }

  // Check if user can review this product (has purchased it)
  if (!canReview.value) {
    send({
      message: t('reviews.purchase_required_message'),
      type: 'error',
    });
    return;
  }

  resetForm();
  showForm.value = true;
};

const showEditReviewForm = (review: ProductReview) => {
  currentReview.value = review;
  showForm.value = true;
};

const hideForm = () => {
  showForm.value = false;
  currentReview.value = null;
  resetForm();
};

const resetForm = () => {
  formError.value = null;
};

const handleSubmitReview = async (reviewData: {
  title: string;
  content: string;
  rating: number;
  criteria: Array<{ id_criteria: number; rating: number }>;
}) => {
  formError.value = null;
  isSubmitting.value = true;

  try {
    if (currentReview.value) {
      const success = await updateProductReview(props.productId, currentReview.value.id, reviewData);
      if (success) {
        send({
          message: t('reviews.review_updated_successfully'),
          type: 'success',
        });
        hideForm();
        // TanStack Query automatically invalidates and refetches
      }
    } else {
      const success = await addProductReview(props.productId, reviewData);

      if (success) {
        send({
          message: t('reviews.review_submitted_successfully'),
          type: 'success',
        });
        hideForm();
        // TanStack Query automatically invalidates and refetches
      }
    }
  } catch (err) {
    formError.value = err instanceof Error ? err.message : t('reviews.error_submitting_review');
    send({
      message: formError.value,
      type: 'error',
    });
  } finally {
    isSubmitting.value = false;
  }
};

const handleDeleteClick = (review: ProductReview) => {
  reviewToDelete.value = review;
  showDeleteConfirmation.value = true;
};

const confirmDeleteReview = async () => {
  if (!reviewToDelete.value) return;

  isDeleting.value = true;

  try {
    const success = await deleteProductReview(props.productId, reviewToDelete.value.id);
    if (success) {
      send({
        message: t('reviews.review_deleted_successfully'),
        type: 'success',
      });
      showDeleteConfirmation.value = false;
      reviewToDelete.value = null;
      // TanStack Query automatically invalidates and refetches
    }
  } catch (err) {
    send({
      message: t('reviews.failed_to_delete_review'),
      type: 'error',
    });
  } finally {
    isDeleting.value = false;
  }
};

const isCurrentUserReview = (review: ProductReview) => {
  if (!customer.value?.email) return false;

  // Try to match by customer ID if available (custom property)
  if ((review as any).id_customer && customer.value.id) {
    return review.is_owner || Number((review as any).id_customer) === Number(customer.value.id);
  }
};

const formatDate = (dateString: string) => {
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  } catch {
    return dateString;
  }
};

// TanStack Query automatically handles data fetching when the component mounts
// No manual initialization needed
</script>

<style scoped>
/* Custom animations */
@keyframes animate-reverse {
  from {
    transform: rotate(360deg);
  }
  to {
    transform: rotate(0deg);
  }
}

.animate-reverse {
  animation: animate-reverse 1s linear infinite;
}

/* Enhanced backdrop blur */
.backdrop-blur-xl {
  backdrop-filter: blur(16px);
}

/* Custom gradient text */
.bg-clip-text {
  -webkit-background-clip: text;
  background-clip: text;
}
</style>
